import * as Long from 'long';
type BigDecimal = number;
type Boolean = boolean;
type DateTime = string;
type Double = number;
type Float = number;
type GUID = string;
type Guid = string;
type Int = number;
type Integer = number;
type Object = object;
type String = string;
type Struct = any;
type Timestamp = string;
type URL = string;
type UUID = string;
type Url = string;
type UserGUID = string;
type UserGuid = string;
type Value = any;
type bigdecimal = number;
type datetime = string;
type double = number;
type float = number;
type guid = string;
type int = number;
type integer = number;
type url = string;
type userguid = string;
type uuid = string;
type JsonMap<T> = { [key: string]: T };

export interface AddStaticResponseFromPeliasRequest {
  staticRequest?: StaticResponseRequest;
}

export interface AddStaticResponseFromPeliasResponse {
  staticResponse?: StaticResponseSearchResponse;
}

export interface AddStaticResponseRequest {
  staticRequest?: StaticResponseRequest;
  staticResponse?: StaticResponseSearchResponse;
}

export interface AddStaticResponseResponse {}

export interface AddressLocation {
  latitude?: Double;
  longitude?: Double;
}

export interface AtlasAddressComponent {
  code?: String;
  name?: String;
}

export interface CommonAddress {
  addressLine?: String;
  addressLine2?: String;
  city?: String;
  country?: String;
  countryFullname?: String;
  formattedAddress?: String;
  geocode?: AddressLocation;
  hint?: String;
  postalCode?: String;
  streetAddress?: StreetAddress;
  subdivision?: String;
  subdivisionFullname?: String;
  subdivisions?: Subdivision[];
}

export interface Country {
  longName?: String;
  shortName?: String;
}

export interface GetAppleEmbedMapRequest {}

export interface GetAppleEmbedMapResponse {
  token?: String;
}

export interface GetAtlasStaticMapRequest {
  center?: String;
  imageSize?: ImageSize;
  language?: String;
  providerName?: ProvidersNames;
  zoom?: Int;
}

export interface GetAtlasStaticMapResponse {
  image?: Uint8Array;
}

export interface GetEmbedMapRequest {
  query?: String;
}

export interface GetEmbedMapResponse {
  url?: String;
}

export interface GetPlaceRequest {
  searchId?: String;
  sessionToken?: String;
}

export interface GetPlaceResponse {
  place?: Place;
}

export interface GetSnapshotMapRequest {
  center?: String;
}

export interface GetSnapshotMapResponse {
  image?: Uint8Array;
}

export interface GetStaticMapRequest {
  center?: String;
  imageSize?: ImageSize;
  language?: String;
  zoom?: Int;
}

export interface GetStaticMapResponse {
  image?: Uint8Array;
}

export interface ImageSize {
  height?: Int;
  width?: Int;
}

export interface IntenalGeolocation {
  lat?: Double;
  lng?: Double;
}

export interface IntenalSublocality {
  level1?: String;
  level2?: String;
  level3?: String;
  level4?: String;
  subLocality?: String;
}

export interface ListPredictionsRequest {
  filterBy?: V2FilterBy[];
  input?: String;
  location?: AddressLocation;
  origin?: AddressLocation;
  radius?: Long;
}

export interface ListPredictionsResponse {
  predictions?: Prediction[];
}

export interface LocationDTO {
  country?: AtlasAddressComponent;
  county?: AtlasAddressComponent;
  formattedAddress?: String;
  geolocation?: IntenalGeolocation;
  locality?: AtlasAddressComponent;
  neighborhood?: AtlasAddressComponent;
  premise?: AtlasAddressComponent;
  proximity?: Proximity;
  routes?: String[];
  state?: AtlasAddressComponent;
  streetAddress?: AtlasAddressComponent;
  streetNumber?: AtlasAddressComponent;
  subPremise?: AtlasAddressComponent;
  sublocality?: IntenalSublocality;
  zipCode?: String;
  zipCodeSuffix?: String;
}

export interface MatchedSubstrings {
  length?: Int;
  offset?: Int;
}

export interface Place {
  address?: CommonAddress;
  placeId?: String;
  types?: String[];
}

export interface PredictRequest {
  countryCodes?: String[];
  input?: String;
  location?: AddressLocation;
  origin?: AddressLocation;
  radius?: Long;
  sessionToken?: String;
}

export interface PredictResponse {
  predictions?: Prediction[];
}

export interface Prediction {
  description?: String;
  distanceInMeters?: Int;
  matchedSubstrings?: MatchedSubstrings[];
  searchId?: String;
  textStructure?: TextStructure;
}

export enum ProvidersNames {
  Google = 'Google',
  Apple = 'Apple',
}

export enum Proximity {
  UNKNOWN_PROXIMITY = 'UNKNOWN_PROXIMITY',
  PINPOINT = 'PINPOINT',
  APPROXIMATE = 'APPROXIMATE',
  GENERAL_AREA = 'GENERAL_AREA',
}

export interface RemoveStaticResponseRequest {
  staticRequest?: StaticResponseRequest;
}

export interface RemoveStaticResponseResponse {}

export interface ReverseGeocodingRequest {
  addressLocation?: AddressLocation;
}

export interface ReverseGeocodingResponse {
  searchResults?: SearchResult[];
}

export interface SearchByZipCodeRequest {
  countryCode?: String;
  zipCode?: String;
}

export interface SearchByZipCodeResponse {
  address?: ServiceAddress;
}

export interface SearchRequest {
  countryCodes?: String[];
  filterBy?: V2FilterBy[];
  query?: String;
  zipCode?: String;
}

export interface SearchRequestV1 {
  filterBy?: ServiceFilterBy[];
  languageCode?: String;
  query?: String;
}

export interface SearchResponse {
  searchResults?: SearchResult[];
}

export interface SearchResponseV1 {
  addresses?: ServiceAddress[];
}

export interface SearchResult {
  address?: CommonAddress;
  proximity?: Proximity;
}

export interface ServiceAddress {
  city?: String;
  country?: Country;
  countryCode?: String;
  formattedAddress?: String;
  geolocation?: ServiceGeolocation;
  neighborhood?: String;
  premise?: String;
  route?: String;
  routes?: String[];
  state?: State;
  streetNumber?: String;
  subAreaOfCity?: String;
  subAreaOfState?: String;
  sublocality?: ServiceSublocality;
  zipCode?: String;
}

export interface ServiceFilterBy {
  filterType?: ServiceFilterByFilterType;
  filterValue?: String;
}

export enum ServiceFilterByFilterType {
  zip_code = 'zip_code',
  country_code = 'country_code',
}

export interface ServiceGeolocation {
  lat?: Double;
  lng?: Double;
}

export interface ServiceSublocality {
  level2?: String;
  level3?: String;
  level4?: String;
}

export interface State {
  longName?: String;
  shortName?: String;
}

export interface StaticResponseRequest {
  countryCodes?: String[];
  languageCode?: String;
  query?: String;
  zipCode?: String;
}

export interface StaticResponseSearchResponse {
  location?: LocationDTO[];
}

export interface StreetAddress {
  apt?: String;
  formattedAddressLine?: String;
  name?: String;
  number?: String;
}

export interface Subdivision {
  code?: String;
  name?: String;
  type?: SubdivisionType;
  typeInfo?: String;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  COUNTRY = 'COUNTRY',
}

export interface TextStructure {
  mainText?: String;
  mainTextMatchedSubstrings?: MatchedSubstrings[];
  secondaryText?: String;
}

export interface V2FilterBy {
  filterType?: V2FilterByFilterType;
  filterValue?: String;
}

export enum V2FilterByFilterType {
  zip_code = 'zip_code',
  country_code = 'country_code',
}
